import {
  startOfWeek,
  endOfWeek,
  endOfDay,
  startOfDay,
  endOfMonth,
  startOfMonth,
  subDays,
  subWeeks,
  subMonths,
  addDays,
  addWeeks,
  addMonths,
  setHours,
  setMinutes,
  format,
  differenceInMilliseconds,
} from "date-fns";
import { ToastOptions } from "react-toastify";
import { BadgeColor } from "@pushpress/shared-components";
import {
  AppointmentAction,
  AppointmentFilter,
  AppointmentStatus,
  AppointmentType,
  AppointmentViewType,
  DateRange,
  Staff,
} from "./Appointments.types";
import { AppointmentEvent } from "../../apis/appointmentApi";

export const calendarViewType = "appointment-calendar-view-type";
export const rememberStaff = "appointment-calendar-remember-staff";
export const rememberFilter = "appointment-calendar-remember-filter";

export function getRangeByType(
  date: Date,
  viewTypeData: AppointmentViewType,
): DateRange {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  switch (viewTypeData) {
    case "multi-staff":
    case "list-day":
      return [dayStart, dayEnd];
    case "single-staff":
    case "list-week":
      return [weekStart, weekEnd];
    case "list-month":
      return [monthStart, monthEnd];
    default:
      return [dayStart, dayEnd];
  }
}

export function moveDateRangeBackward(
  dateRange: [Date, Date],
  viewTypeData: AppointmentViewType,
): DateRange {
  switch (viewTypeData) {
    case "multi-staff":
    case "list-day": {
      const start = subDays(dateRange[0], 1);
      const end = subDays(dateRange[1], 1);
      return [start, end];
    }
    case "single-staff":
    case "list-week": {
      const start = subWeeks(dateRange[0], 1);
      const end = subWeeks(dateRange[1], 1);
      return [start, end];
    }
    default: {
      const start = subMonths(dateRange[0], 1);
      const end = subMonths(dateRange[1], 1);
      return [start, end];
    }
  }
}

export function moveDateRangeForward(
  dateRange: [Date, Date],
  viewTypeData: AppointmentViewType,
): DateRange {
  switch (viewTypeData) {
    case "multi-staff":
    case "list-day": {
      const start = addDays(dateRange[0], 1);
      const end = addDays(dateRange[1], 1);
      return [start, end];
    }
    case "single-staff":
    case "list-week": {
      const start = addWeeks(dateRange[0], 1);
      const end = addWeeks(dateRange[1], 1);
      return [start, end];
    }
    default: {
      const start = addMonths(dateRange[0], 1);
      const end = addMonths(dateRange[1], 1);
      return [start, end];
    }
  }
}

export function filterAvailableStaff(
  staff: Staff[],
  appointmentTypes: AppointmentType[],
) {
  if (appointmentTypes && appointmentTypes.length && staff && staff.length) {
    const AppointmentStaffs = appointmentTypes
      .map((appointmentType) => appointmentType.staff)
      .flat();

    const staffWithAppointment = staff.filter((entry) => {
      return AppointmentStaffs.some((item) => item.uuid === entry.user_uuid);
    });

    if (staffWithAppointment.length > 0) {
      return staffWithAppointment;
    }

    return staff;
  }
  return [];
}

export function setToTheNextHour(date) {
  const now = new Date();
  const withHours = setHours(date, now.getHours());
  if (now.getMinutes() === 0) {
    return setMinutes(withHours, now.getMinutes());
  } else {
    return setMinutes(withHours, 60);
  }
}

export function isViewTypeList(type: AppointmentViewType) {
  return type === "list-day" || type === "list-week" || type === "list-month";
}

export function dateToDateInputValue(date: Date) {
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

export function isFilterEmpty(filter: AppointmentFilter) {
  if (filter.member) {
    return false;
  }
  if (filter.appointmentTypes?.length > 0) {
    return false;
  }
  if (filter.staff?.length > 0) {
    return false;
  }
  return true;
}

export const appointmentFilterDefault: AppointmentFilter = {
  member: null,
  appointmentTypes: [],
  staff: [],
  rememberFilter: false,
};

export function filterValidBlockedData(
  blockedData?: {
    start: number;
    end: number;
    key: string;
  }[],
) {
  return (
    blockedData?.filter((item) => item.start && item.end && item.key) || []
  );
}

export const defaultToastOptions: ToastOptions = {
  position: "bottom-center",
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  progress: undefined,
};

interface Appointment {
  startTime: string;
  appointmentType: {
    lateCancel: number;
  };
}

export function isLateCancelWindow(appointment?: Appointment) {
  if (!appointment) {
    return false;
  }
  const { startTime, appointmentType: { lateCancel } = {} } = appointment;
  if (!startTime || lateCancel === undefined || lateCancel === null) {
    return false;
  }
  const start = new Date(startTime);
  const diffInSeconds = differenceInMilliseconds(start, new Date()) / 1000;
  return diffInSeconds <= lateCancel;
}

export const appointmentActionsMap: {
  [key in AppointmentStatus]: AppointmentAction[];
} = {
  CANCELLED: ["info"],
  CHECKEDIN: ["changeStaff", "reschedule", "cancel"],
  EXPIRED: ["delete", "changeExpiration"],
  NOTBOOKED: ["delete", "changeExpiration"],
  NOSHOW: ["checkIn", "changeStaff", "reschedule", "cancel"],
  SCHEDULED: ["checkIn", "noShow", "changeStaff", "reschedule", "cancel"],
};

export function getAppointmentStatusName(appointment: {
  eventStatus: AppointmentEvent["eventStatus"];
  isLateCancel?: boolean | null;
  appointmentCredit?: object | null;
  unpaidReason?: string | null;
}) {
  const status = appointment.eventStatus;
  const isLateCancel = !!appointment.isLateCancel;
  const isPaid = !!appointment.appointmentCredit;
  const hasUnpaidReason = !!appointment.unpaidReason;

  switch (status) {
    case "CANCELLED": {
      if (isLateCancel) {
        if (isPaid) {
          return "LATE CANCEL";
        } else {
          if (hasUnpaidReason) {
            return "FORGIVEN";
          } else {
            return "UNPAID LATE CANCEL";
          }
        }
      } else {
        return "CANCELLED";
      }
    }
    case "CHECKEDIN": {
      return "CHECKED IN";
    }
    case "EXPIRED": {
      return "EXPIRED";
    }
    case "NOTBOOKED": {
      return "ACTIVE";
    }
    case "NOSHOW": {
      if (isPaid) {
        return "NO SHOW";
      } else {
        if (hasUnpaidReason) {
          return "FORGIVEN";
        } else {
          return "UNPAID NO SHOW";
        }
      }
    }
    case "SCHEDULED": {
      if (isPaid) {
        return "BOOKED";
      } else {
        return "BOOKED (UNPAID)";
      }
    }
    default: {
      return exhaustiveGuard(status);
    }
  }
}

function exhaustiveGuard(status: never): never {
  throw new Error(
    `ERROR! Reached forbidden guard function with unexpected value: ${JSON.stringify(
      status,
    )}`,
  );
}

export type AppointmentStatusName = ReturnType<typeof getAppointmentStatusName>;

export const colorByAppointmentStatusName: Record<
  AppointmentStatusName,
  BadgeColor
> = {
  CANCELLED: BadgeColor.Error,
  EXPIRED: BadgeColor.Error,
  "CHECKED IN": BadgeColor.Success,
  "NO SHOW": BadgeColor.Error,
  ACTIVE: BadgeColor.Success,
  "LATE CANCEL": BadgeColor.Error,
  FORGIVEN: BadgeColor.Gray,
  "UNPAID LATE CANCEL": BadgeColor.Warning,
  "UNPAID NO SHOW": BadgeColor.Warning,
  BOOKED: BadgeColor.Info,
  "BOOKED (UNPAID)": BadgeColor.Gray,
};
